import React, { TextareaHTMLAttributes, useId } from 'react';
import { Icon } from './Icon';
import RequiredMark from './RequiredMark';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    /** Forwarded to the underlying <textarea>. Works as a regular prop in React 19. */
    ref?: React.Ref<HTMLTextAreaElement>;
    label?: string;
    error?: string;
    size?: 'default' | 'compact';
    /**
     * Marks the field as private/internal: amber label with a lock icon and
     * amber border tint. See Standards.md §"Private / internal fields".
     */
    isPrivate?: boolean;
    /** Required field: a red asterisk after the label and `aria-required`
     *  (Standards §14.7). Not `required`, which on an input is the browser's
     *  own validation and blocks the form with its bubble. */
    markRequired?: boolean;
}

const Textarea: React.FC<TextareaProps> = ({ label, error, size = 'default', isPrivate = false, markRequired = false, className = '', ref, ...props }) => {
    const autoId = useId();
    const fieldId = props.id ?? autoId;
    const errorId = `${fieldId}-error`;
    const ariaLabel = props['aria-label'] ?? (!label && typeof props.placeholder === 'string' ? props.placeholder : undefined);
    const describedBy = [props['aria-describedby'], error ? errorId : null].filter(Boolean).join(' ') || undefined;
    const labelTextClass = isPrivate
        ? 'text-amber-600 dark:text-amber-500'
        : 'text-primary';
    const borderClass = error
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
        : isPrivate
            ? 'border-border-strong dark:border-border-strong-dark hover:border-border-strong dark:hover:border-border-strong-dark focus:ring-amber-500/20 focus:border-amber-500'
            : 'border-border-strong dark:border-border-strong-dark hover:border-border-strong dark:hover:border-border-strong-dark focus:ring-primary focus:border-primary';
    return (
        <div className={`w-full relative ${label ? 'pt-2' : ''}`}>
            {label && (
                // Truncates on a narrow field, so it carries its own text as a
                // tooltip (see `Input`); clicking it focuses the field.
                <label htmlFor={fieldId} title={typeof label === 'string' ? label : undefined} className={`absolute top-0 left-2 px-1 bg-white dark:bg-surface-dark text-[10px] font-bold ${labelTextClass} uppercase tracking-wider z-10 truncate max-w-[calc(100%-1rem)] flex items-center gap-0.5 cursor-text`}>
                    {isPrivate && (
                        <Icon name="lock" className="!text-[10px]" aria-hidden="true" />
                    )}
                    {label}
                    {markRequired && <>&nbsp;<RequiredMark /></>}
                </label>
            )}
            <textarea
                className={`
                    w-full rounded-md border
                    bg-transparent
                    text-text-main dark:text-white
                    px-3 ${size === 'compact' ? 'py-1.5' : 'py-2.5'} text-sm
                    placeholder-slate-400 dark:placeholder-slate-600
                    focus:ring-1
                    outline-none transition-all
                    shadow-sm
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${borderClass}
                    ${className}
                `}
                aria-required={markRequired || props['aria-required'] || undefined}
                {...props}
                ref={ref}
                id={fieldId}
                aria-label={ariaLabel}
                aria-invalid={error ? true : props['aria-invalid']}
                aria-describedby={describedBy}
            />
            {error && <p id={errorId} role="alert" className="text-red-500 text-xs mt-1 font-medium">{error}</p>}
        </div>
    );
};

export default Textarea;
